import { styled } from '@linaria/react';
import { fonts, layout, primitives, semantic, size, space } from '../../theme/tokens';
import { Button } from '../Button';
import { EntityImage } from '../EntityImage';

export const CardWrapper = styled.div<{ $unavailable?: boolean; $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space['space-600']};
  padding: ${space['space-600']};
  background: ${({ $unavailable }) =>
    $unavailable ? semantic.bg.surfaceSubtle : semantic.bg.canvas};
  border: ${size.stroke['stroke-default']} solid
    ${({ $unavailable }) => ($unavailable ? 'transparent' : semantic.border.default)};
  border-radius: ${size.radius['radius-lg']};
  box-sizing: border-box;
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    background: ${semantic.bg.surfaceSubtle};
    border-color: transparent;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  @media (max-width: ${layout.desktopBreakpoint}) {
    align-items: flex-start;
    gap: ${space['space-300']};
  }
`;

export const Thumbnail = styled.div`
  width: 74px;
  height: 74px;
  border-radius: ${size.radius['radius-xs']};
  overflow: hidden;
  flex-shrink: 0;
  background: ${semantic.bg.surfaceSubtle};
`;

export const ThumbnailImage = styled(EntityImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: ${semantic.bg.surfaceSubtle};
`;

export const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
  min-width: 0;
  flex: 1;
`;

export const Name = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.normal};
  color: ${semantic.text.primary};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};

  @media (max-width: ${layout.desktopBreakpoint}) {
    font-size: ${fonts.size.sm};
  }
`;

export const Label = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.base};
  color: ${semantic.text.secondary};
  line-height: ${primitives.typography.lineHeight.normal};
  letter-spacing: ${primitives.typography.tracking.normal};

  @media (max-width: ${layout.desktopBreakpoint}) {
    font-size: ${fonts.size.sm};
  }
`;

export const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${space['space-600']};
  flex-shrink: 0;

  @media (max-width: ${layout.desktopBreakpoint}) {
    flex-direction: column;
    align-items: flex-end;
    gap: ${space['space-200']};
  }
`;

export const PriceWrapper = styled.div`
  font-family: ${fonts.body};
  font-size: ${fonts.size.base};
  color: ${semantic.text.primary};
  text-align: right;
  white-space: nowrap;
`;

export const ActionButton = styled(Button)`
  @media (max-width: ${layout.desktopBreakpoint}) {
    && {
      height: ${primitives.size['size-1000']};
      padding: ${space['space-100']} ${space['space-400']};
    }
  }
`;

export const ActionButtonLabel = styled.span`
  @media (max-width: ${layout.desktopBreakpoint}) {
    display: none;
  }
`;

export const ActionButtonIcon = styled.span`
  display: none;

  @media (max-width: ${layout.desktopBreakpoint}) {
    display: inline-flex;
    align-items: center;
  }
`;

export const UnavailableBody = styled.div`
  display: flex;
  align-items: center;
  gap: ${space['space-600']};
  flex: 1;
  min-width: 0;

  @media (max-width: ${layout.desktopBreakpoint}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${space['space-200']};
  }
`;

export const UnavailableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${space['space-400']};
  flex: 1;
  min-width: 0;

  @media (max-width: ${layout.desktopBreakpoint}) {
    align-items: flex-start;
  }
`;

export const UnavailableTextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space['space-200']};
  height: ${primitives.size['size-1000']};
  padding: ${space['space-200']} ${space['space-400']};
  background: ${semantic.bg.secondary};
  border-radius: ${size.radius['radius-full']};
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: ${layout.desktopBreakpoint}) {
    width: 100%;
  }
`;

export const UnavailableDivider = styled.span`
  width: ${size.stroke['stroke-default']};
  height: ${primitives.size['size-400']};
  background: ${semantic.border.default};
  flex-shrink: 0;
`;

export const UnavailableText = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  color: ${semantic.text.primary};
  line-height: 1;
  letter-spacing: ${primitives.typography.tracking.normal};
  white-space: nowrap;
`;

export const EditSearchButton = styled.button`
  font-family: ${fonts.body};
  font-weight: ${fonts.weight.medium};
  font-size: ${fonts.size.sm};
  color: ${semantic.text.primary};
  line-height: 1;
  letter-spacing: ${primitives.typography.tracking.normal};
  white-space: nowrap;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: ${semantic.text.brand};
  }
`;
